import React, { useCallback, useEffect, useState } from 'react';
import { Box, Menu, MenuItem } from '@chakra-ui/react';
import { Colors, Icon, Text } from '@mercator-gcp/grout';
import useOnclickOutside from 'react-cool-onclickoutside';
import usePlacesAutocomplete, { getGeocode, getLatLng } from 'use-places-autocomplete';
import SearchInput from 'components/elements/SearchInput';
import { FlexRow } from 'components/layout/FlexRow';

const suggestionsBox = {
  position: 'absolute',
  top: '40px',
  left: 0,
  background: 'white',
  padding: '10px',
  width: '100%',
  borderRadius: '4px',
  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
  zIndex: 10,
};

const menuRow = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'padding_4',
};

export interface AddressCoords {
  lat: number;
  lng: number;
}

interface AddressAutoCompleteProps {
  onSelect: (address: string, coords: AddressCoords) => void;
  onClear?: () => void;
  placeholder?: string;
  externalValue?: string;
  sx?: React.CSSProperties;
}

const AddressAutoComplete = ({
  onSelect,
  onClear,
  placeholder = 'Search by address',
  externalValue,
  sx,
}: AddressAutoCompleteProps) => {
  const [showSuggestions, setShowSuggestions] = useState(false);

  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: { country: ['us'] },
    },
    debounce: 300,
  });

  useEffect(() => {
    if (externalValue !== undefined) {
      setValue(externalValue, false);
    }
  }, [externalValue, setValue]);

  const ref = useOnclickOutside(() => {
    clearSuggestions();
    setShowSuggestions(false);
  });

  const handleChange = useCallback(
    (inputValue: string) => {
      setValue(inputValue);
      setShowSuggestions(true);
    },
    [setValue],
  );

  const handleClear = useCallback(() => {
    setValue('', false);
    clearSuggestions();
    setShowSuggestions(false);
    onClear?.();
  }, [setValue, clearSuggestions, onClear]);

  const handleSelect = useCallback(
    ({ description }: { description: string }) => {
      setValue(description, false);
      clearSuggestions();
      setShowSuggestions(false);

      getGeocode({ address: description }).then((results) => {
        const { lat, lng } = getLatLng(results[0]);
        onSelect(description, { lat, lng });
      });
    },
    [setValue, clearSuggestions, onSelect],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && data.length > 0) {
        handleSelect(data[0]);
      }
    },
    [data, handleSelect],
  );

  const renderSuggestions = () =>
    data.map((suggestion) => {
      const {
        place_id,
        structured_formatting: { main_text, secondary_text },
      } = suggestion;

      return (
        <MenuItem
          key={place_id}
          onClick={() => handleSelect(suggestion)}
          autoFocus={false}
        >
          <Box>
            <FlexRow sx={menuRow}>
              <Icon name={Icon.Name.MapMarker} size={20} />
              <Text.BodyBold as="div" sx={{ color: Colors.ForestGreen900 }}>
                {main_text}
              </Text.BodyBold>
            </FlexRow>
            <Text as="div" sx={{ color: Colors.ForestGreen900 }}>
              {secondary_text}
            </Text>
          </Box>
        </MenuItem>
      );
    });

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%' }}>
      <SearchInput
        value={value}
        onChange={handleChange}
        onClear={handleClear}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={!ready}
        sx={sx}
      />
      {status === 'OK' && showSuggestions && (
        <Menu isOpen={data.length > 0} autoSelect={false}>
          <Box sx={suggestionsBox}>{renderSuggestions()}</Box>
        </Menu>
      )}
    </div>
  );
};

export default AddressAutoComplete;
